import type { CreatorLookupStatus, MetricsStatus, SnapshotMetrics } from '@discovr/types';
import type { ContentMetricsCopy } from './contentMetricsCopy';

export const STALLED_PENDING_MS = 60 * 60 * 1000;

/** The only SnapshotMetrics fields the caption/manual-equivalence/stall
 *  logic below ever reads — narrow enough that a caller assembling one from
 *  a different row shape (e.g. OutreachRow) can pass a plain object literal
 *  instead of an `as SnapshotMetrics` cast. */
export type MetricsCaptionSnapshot = Pick<SnapshotMetrics, 'metrics_status' | 'avg_views' | 'engagement_rate' | 'metrics_pending_since'>;

export type MetricsCaptionTone = 'muted' | 'amber' | 'danger';
// amber-700, not amber-600: amber-600 is ~3.18:1 on the paper background,
// under the 4.5:1 WCAG 1.4.3 floor for 12px caption type.
export const TONE_CLASS: Record<MetricsCaptionTone, string> = {
  muted: 'text-muted-foreground',
  amber: 'text-amber-700',
  danger: 'text-destructive',
};
export type MetricsCaptionKind = 'manual' | 'pending' | 'stalled' | 'no_reels' | 'failed' | 'not_fetched';
export type MetricsCaption = {
  label: string;
  tone: MetricsCaptionTone;
  kind: MetricsCaptionKind;
  spinner?: boolean;
} | null;

/** Twin of the BE's notManualEquivalent filter: explicit manual, or a legacy
 *  row whose values can only have come from a sheet. */
export function isManualEquivalent(m: MetricsCaptionSnapshot): boolean {
  if (m.metrics_status === 'manual') return true;
  return !m.metrics_status && ((m.engagement_rate ?? 0) > 0 || (m.avg_views ?? 0) > 0);
}

/** `now` is epoch milliseconds throughout this module — see useNow. */
export function isStalledPending(m: MetricsCaptionSnapshot, now: number): boolean {
  if (m.metrics_status !== 'pending' || !m.metrics_pending_since) return false;
  const since = Date.parse(m.metrics_pending_since);
  return Number.isFinite(since) && now - since >= STALLED_PENDING_MS;
}

const MANUAL_CAPTION: MetricsCaption = { label: 'manual', tone: 'muted', kind: 'manual' };
const STALLED_CAPTION: MetricsCaption = { label: 'Fetch stalled', tone: 'amber', kind: 'stalled' };
const FETCHING_CAPTION: MetricsCaption = { label: 'Fetching…', tone: 'muted', kind: 'pending', spinner: true };
const FAILED_CAPTION: MetricsCaption = { label: 'Fetch failed', tone: 'danger', kind: 'failed' };
const NOT_FETCHED_CAPTION: MetricsCaption = { label: 'Not fetched', tone: 'muted', kind: 'not_fetched' };

/** Compile-time exhaustiveness guard: a status added to MetricsStatus without
 *  a case of its own stops this assignment — and the build — rather than
 *  falling through to another status's label or, worse, its paid re-fetch. */
function unhandledStatus<T>(_status: never, fallback: T): T {
  return fallback;
}

/** `copy` words the no-content caption for the campaign's platform.
 *  `settling`: a re-check for this row is in flight (profileRecheckStore).
 *  On retry of an already-stalled row the BE keeps the old pending_since, so
 *  isStalledPending would still read it as stalled — settling overrides that
 *  back to the normal fetching caption while the fresh job runs. */
export function resolveMetricsCaption(
  m: MetricsCaptionSnapshot,
  lookup: CreatorLookupStatus | undefined,
  now: number,
  copy: Pick<ContentMetricsCopy, 'noContent'>,
  settling = false,
): MetricsCaption {
  if (lookup !== 'active') return null;
  if (m.metrics_status === 'manual') return MANUAL_CAPTION;
  if (isManualEquivalent(m) || m.metrics_status === 'fetched') return null;
  const status: MetricsStatus | undefined = m.metrics_status;
  // No status at all: the row has never been queued.
  if (!status) return NOT_FETCHED_CAPTION;
  switch (status) {
    case 'pending':
      return !settling && isStalledPending(m, now) ? STALLED_CAPTION : FETCHING_CAPTION;
    case 'no_reels':
      return { label: copy.noContent, tone: 'amber', kind: 'no_reels' };
    case 'failed':
      return FAILED_CAPTION;
    default:
      // 'manual' and 'fetched' are returned above, so TS has already narrowed
      // them out here; anything else is a status the BE added first.
      return unhandledStatus(status, NOT_FETCHED_CAPTION);
  }
}

/** ⟳ on Avg Views: profile active, nothing manual/fetched, not mid-fetch.
 *  Decided per known status — an unknown one must not offer a paid re-fetch. */
export function showMetricsRecheck(m: SnapshotMetrics, lookup: CreatorLookupStatus | undefined, now: number): boolean {
  if (lookup !== 'active' || isManualEquivalent(m)) return false;
  const status: MetricsStatus | undefined = m.metrics_status;
  // Never queued — offering the fetch is the whole point of the button.
  if (!status) return true;
  switch (status) {
    case 'pending':
      return isStalledPending(m, now);
    case 'no_reels':
    case 'failed':
      return true;
    case 'manual':
    case 'fetched':
      return false;
    default:
      return unhandledStatus(status, false);
  }
}

/** Est. CPV cell: a skeleton while the reel fetch is running; "—" when there
 *  is nothing to estimate from. Never a 0. A stalled pending row is not still
 *  running — every sibling rule already reads it as stalled, and an animated
 *  skeleton next to an "Fetch stalled" caption would pulse forever. */
export function estimateDisplayState(
  m: SnapshotMetrics | undefined,
  now: number,
  settling = false,
): 'loading' | 'empty' | 'value' {
  if (!m) return 'empty';
  if (m.metrics_status === 'pending') return !settling && isStalledPending(m, now) ? 'empty' : 'loading';
  if ((m.avg_views ?? 0) > 0) return 'value';
  return 'empty';
}

/** A row's latest metrics fetch as the re-check spinner tracks it. The
 *  profile scope tracks the same pair, so profileRecheckStore's `Baseline`
 *  aliases this rather than restating it. */
export type MetricsBaseline = Readonly<{ status: string | undefined; checkedAt: string | undefined }>;

export function rowMetrics(m: SnapshotMetrics | undefined): MetricsBaseline {
  return { status: m?.metrics_status, checkedAt: m?.metrics_checked_at };
}

export function fetchedAtLabel(m: SnapshotMetrics): string | null {
  if (m.metrics_status !== 'fetched' || !m.metrics_checked_at) return null;
  const d = new Date(m.metrics_checked_at);
  if (Number.isNaN(d.getTime())) return null;
  return `Fetched ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}
